/* ap-personalize.js — Smart landing page personalization (Alkaline Peptides).
 * Campaign email links carry &apx=<token> (the recipient's HMAC token — no PII)
 * and/or &apseg=<segment> (coarse interest segment: glp1|recovery|longevity|womens).
 * apx → fetch profile (name + ranked purchase history + stock) from the Halo API.
 * apseg → pure client-side: segment hero headline + product-grid reorder, no API.
 * 100% graceful: without params, or on any error, the page stays as shipped. */
(function () {
  'use strict';
  var API = 'https://app.securehalo.ai/api/mobile/landing-profile';
  var KEY = 'ap_apx_token';
  var SEG_KEY = 'ap_apseg';

  // ── Interest segments: product order (present handles float first) + hero copy ──
  var SEGMENTS = {
    glp1: {
      handles: ['glp1t', 'reta-30mg', 'glp-1s', 'motd-c-10mg', 'tesamorelin-10mg'],
      hero: ['THE WEIGHT-LOSS', 'PEPTIDES', 'YOU\u2019VE BEEN READING ABOUT'],
      heroNamed: function (n) { return ['THE WEIGHT-LOSS', 'PEPTIDES', n + ', YOU\u2019VE BEEN READING ABOUT']; }
    },
    recovery: {
      handles: ['prohealix-bpc157-10mg-tb500-10mg', 'bpc157-10mg', 'tb500-5mg', 'cjc1295-no-dac-10mg-ipemorelin-15mg-5ml', 'shgh-10iu'],
      hero: ['REPAIR.', 'RECOVER.', 'REBUILD.']
    },
    longevity: {
      handles: ['epitalon-100mg', 'nads-500mg', 'ss-31-50mg', 'dsip-delta-sleep-inducing-peptide-10mg', 'motd-c-10mg'],
      hero: ['THE LONGEVITY', 'STACK', 'FINAL VIALS']
    },
    womens: {
      handles: ['ghk-cu-50mg', 'collagen-10mg', 'glow-70mg-bpc-157-10mg-tb500-10mg-ghk-cu-50mg', 'oxytocin-10mg'],
      hero: ['GLOW, RECOVER,', 'RENEW', 'FINAL STOCK']
    }
  };

  // ── Token: URL param wins, else sessionStorage (so navigation keeps it) ──
  var token = null;
  try {
    var m = location.search.match(/[?&]apx=([a-f0-9]{16,64})/);
    if (m) { token = m[1]; sessionStorage.setItem(KEY, token); }
    else token = sessionStorage.getItem(KEY);
  } catch (e) { /* storage blocked — param-only */ }
  if (token && !/^[a-f0-9]{16,64}$/.test(token)) token = null;

  // ── Segment: ?apseg= param wins, else localStorage (sticks across visits) ──
  var segment = null;
  try {
    var sm = location.search.match(/[?&]apseg=([a-z0-9-]{2,32})/);
    if (sm && SEGMENTS[sm[1]]) { segment = sm[1]; localStorage.setItem(SEG_KEY, segment); }
    else {
      var stored = localStorage.getItem(SEG_KEY);
      if (stored && SEGMENTS[stored]) segment = stored;
    }
  } catch (e) { /* storage blocked — param-only */ if (!segment && sm && SEGMENTS[sm[1]]) segment = sm[1]; }

  // ── SMS click beacon: alkalinepeptides.com/<n> redirects land with ?pcref=<n>.
  // Fire-and-forget GET to the Halo API logs the click (sms_link_hits). Once per
  // pcref per session; never blocks or breaks the page. ──
  try {
    var pm = location.search.match(/[?&]pcref=([A-Za-z0-9]{1,16})/);
    if (pm) {
      var pcKey = 'ap_pcref_sent_' + pm[1];
      if (!sessionStorage.getItem(pcKey)) {
        sessionStorage.setItem(pcKey, '1');
        new Image().src = API.replace('/landing-profile', '/sms-hit') + '?pcref=' + encodeURIComponent(pm[1]);
      }
    }
  } catch (e) { /* never break the page for tracking */ }

  if (!token && !segment) return;

  var esc = function (s) {
    var d = document.createElement('div');
    d.textContent = String(s == null ? '' : s);
    return d.innerHTML;
  };

  function fetchProfile() {
    return fetch(API + '?t=' + encodeURIComponent(token), { mode: 'cors' })
      .then(function (r) { if (!r.ok) throw new Error('profile ' + r.status); return r.json(); });
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  // ── Segment: hero headline swap (text only — layout/font untouched) ──
  function segmentHero(seg, firstName) {
    var cfg = SEGMENTS[seg];
    if (!cfg) return;
    var hero = document.querySelector('.ap-hero');
    if (!hero) return;
    var lines = hero.querySelectorAll('.ap-hero__headline-line');
    if (lines.length < 3) return;
    var name = (firstName || '').trim();
    var copy = (name && cfg.heroNamed) ? cfg.heroNamed(name.toUpperCase()) : cfg.hero;
    for (var i = 0; i < 3; i++) lines[i].textContent = copy[i];
  }

  // ── Segment: float the segment's products to the front of a grid (stable) ──
  function reorderByHandles(grid, cardSel, handles) {
    if (!grid) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll(cardSel));
    if (!cards.length) return;
    var rank = function (el) {
      var i = handles.indexOf(el.getAttribute('data-ap-handle'));
      return i === -1 ? 999 : i;
    };
    cards.map(function (el, i) { return { el: el, r: rank(el), i: i }; })
      .sort(function (a, b) { return (a.r - b.r) || (a.i - b.i); })
      .forEach(function (x) { grid.appendChild(x.el); });
  }

  // ── Segment: reorder the hero's per-vial images so the segment's vials
  // come first. The 5 visible imgs define fixed "slots" (left/bottom/height);
  // we re-assign which vial occupies which slot. Hidden extra vials (e.g.
  // Tirzepatide/Semaglutide) get pulled in when the segment wants them.
  function reorderHeroVials(seg) {
    var cfg = SEGMENTS[seg];
    var stack = document.querySelector('[data-ap-vials]');
    if (!cfg || !stack) return;
    var all = Array.prototype.slice.call(stack.querySelectorAll('[data-vial-handle]'));
    var visible = all.filter(function (el) { return !el.hidden; });
    if (visible.length < 2) return;
    var slots = visible.map(function (el) {
      return { left: el.style.left, bottom: el.style.bottom, height: el.style.height };
    });
    var byHandle = {};
    all.forEach(function (el) { byHandle[el.getAttribute('data-vial-handle')] = el; });
    var order = [];
    cfg.handles.forEach(function (h) {
      if (byHandle[h] && order.indexOf(byHandle[h]) === -1) order.push(byHandle[h]);
    });
    visible.forEach(function (el) { if (order.indexOf(el) === -1) order.push(el); });
    order = order.slice(0, slots.length);
    all.forEach(function (el) {
      var i = order.indexOf(el);
      if (i === -1) { el.hidden = true; return; }
      el.hidden = false;
      el.style.left = slots[i].left;
      el.style.bottom = slots[i].bottom;
      el.style.height = slots[i].height;
    });
  }

  function applySegment(seg, firstName) {
    var cfg = SEGMENTS[seg];
    if (!cfg) return;
    segmentHero(seg, firstName);
    try { reorderHeroVials(seg); } catch (e) { /* keep default vials */ }
    reorderByHandles(document.querySelector('.ap-products__grid'), '[data-ap-handle]', cfg.handles);
    document.documentElement.setAttribute('data-ap-segment', seg);
  }

  // ── a+b) Hero: personalize the layered hero's real text elements ──
  function personalizeHero(p, seg) {
    var hero = document.querySelector('.ap-hero');
    if (!hero) return;
    var name = p.first_name;
    // If a segment headline is active, the segment copy owns the headline
    // (name is woven in by segmentHero via heroNamed). Otherwise: NAME IS HERE.
    if (!seg) {
      var nameLine = hero.querySelector('[data-ap-hero-name-line]');
      if (nameLine && name) nameLine.textContent = name.toUpperCase() + ' IS HERE';
    }

    var favs = (p.favorites || []).filter(function (f) { return f.qty_left > 0; });
    var own = p.has_history && favs.length;
    var sub = hero.querySelector('[data-ap-hero-subline]');
    if (sub && own && name) {
      var line;
      if (favs.length >= 2) {
        line = 'Only <strong>' + esc(favs[0].qty_left) + '</strong> of your favourite <strong>' + esc(favs[0].title) +
          '</strong> and <strong>' + esc(favs[1].qty_left) + '</strong> of <strong>' + esc(favs[1].title) +
          '</strong> remain, ' + esc(name) + ' \u2014 hurry.';
      } else {
        line = 'Only <strong>' + esc(favs[0].qty_left) + '</strong> of your favourite <strong>' + esc(favs[0].title) +
          '</strong> remain, ' + esc(name) + ' \u2014 hurry.';
      }
      sub.innerHTML = line;
    }
  }

  // ── c) Featured products: their favourites first + retitle ──
  function personalizeProducts(p) {
    var favs = (p.favorites || []).map(function (f) { return f.handle; });
    if (!p.has_history || !favs.length) return;
    var grid = document.querySelector('.ap-products__grid');
    if (!grid) return;
    reorderByHandles(grid, '[data-ap-handle]', favs);
    if (p.first_name) {
      var h2 = document.querySelector('.ap-products__heading h2');
      if (h2) h2.textContent = 'Your favourites, ' + p.first_name;
      var eyebrow = document.querySelector('.ap-products__eyebrow');
      if (eyebrow) eyebrow.textContent = 'Picked from your past orders';
    }
  }

  // ── d) Popular pairings: surface the pairs THEY bought together (if the
  // section already renders them); otherwise leave the default untouched. ──
  function personalizePairings(p) {
    if (!p.pairings || !p.pairings.length) return;
    var grid = document.querySelector('.ap-pairings__grid');
    if (!grid) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-ap-pair]'));
    if (!cards.length) return;
    var mine = p.pairings.map(function (pr) {
      return pr.products.map(function (x) { return x.handle; }).sort().join('|');
    });
    var rank = function (el) {
      var k = (el.getAttribute('data-ap-pair') || '').split('|').sort().join('|');
      var i = mine.indexOf(k);
      return i === -1 ? 999 : i;
    };
    var matched = cards.some(function (el) { return rank(el) !== 999; });
    if (!matched) return; // no overlap with curated pairs → keep default
    cards.sort(function (a, b) { return rank(a) - rank(b); })
      .forEach(function (el) { grid.appendChild(el); });
    var h2 = document.querySelector('.ap-pairings__heading h2');
    if (h2 && p.first_name) h2.textContent = 'Pairs you\u2019ve ordered before, ' + p.first_name;
    var eb = document.querySelector('.ap-pairings__heading > p');
    if (eb) eb.textContent = 'From your own order history';
  }

  // ── e) Final Notice CTA section (Liquid ships generic copy; we personalize) ──
  function personalizeFinalNotice(p) {
    var el = document.getElementById('ap-final-notice');
    if (!el) return;
    var h = el.querySelector('[data-ap-fn-heading]');
    if (h && p.first_name) {
      h.textContent = 'Final Notice, ' + p.first_name + ' — Once these products are gone, they are gone for good.';
    }
  }

  // ── Exit popup stage 1: name + their top favourite ──
  function personalizeExit(p) {
    if (!p.first_name) return;
    var stage = document.querySelector('#ap-exit [data-ap-stage="1"]');
    if (!stage) return;
    var title = stage.querySelector('.ap-exit__title');
    var favs = (p.favorites || []).filter(function (f) { return f.qty_left > 0; });
    if (title) {
      title.textContent = p.has_history && favs.length
        ? 'Don\u2019t go, ' + p.first_name + ' — only ' + favs[0].qty_left + ' of your favourite ' + favs[0].title + ' left'
        : 'Don\u2019t go yet, ' + p.first_name + ' — the final vials are almost gone';
    }
  }

  // ── Segment-only mode: no token → no API call, apply segment immediately ──
  if (!token) {
    onReady(function () {
      try { applySegment(segment, null); } catch (e) { /* never break the page */ }
    });
    window.APSegment = segment;
    return;
  }

  fetchProfile().then(function (p) {
    if (!p) return;
    // Resolved segment: explicit ?apseg= wins, else the profile's interest segment
    // (ahr_segment: 'ahr-glp1' → 'glp1' etc.)
    var seg = segment;
    if (!seg && p.ahr_segment) {
      var s = String(p.ahr_segment).replace(/^ahr-/, '');
      if (SEGMENTS[s]) { seg = s; try { localStorage.setItem(SEG_KEY, s); } catch (e) {} }
    }
    window.APSegment = seg || null;
    onReady(function () {
      try {
        var isHome = document.querySelector('.ap-hero') !== null;
        if (isHome) {
          if (seg) applySegment(seg, p.first_name);
          personalizeHero(p, seg);
          if (p.has_history && (p.favorites || []).length) personalizeProducts(p);
          personalizePairings(p);
        }
        personalizeFinalNotice(p); // sitewide — banner renders on all pages
        personalizeExit(p);
        document.documentElement.setAttribute('data-ap-personalized', 'true');
      } catch (e) { /* never break the page */ }
    });
  }).catch(function () {
    // API down / bad token — still honor the segment
    if (segment) onReady(function () {
      try { applySegment(segment, null); } catch (e) { /* noop */ }
    });
  });
})();
